import { IonIcon, IonSelect, IonSelectOption } from '@ionic/react';
import clsx from 'clsx';
import Avatar from 'components/Avatar';
import Notifications from 'components/Notifications';
import { useWorkspace } from 'hooks/useWorkspace';
import { chevronDownOutline } from 'ionicons/icons';
import React from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
    padding: '14px',
  },
  workspaceItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
    minWidth: 0,
    padding: '9px 10px',
    border: 'none',
    borderRadius: 12,
    background:
      'color-mix(in srgb, var(--ion-toolbar-background, var(--ion-background-color, #fff)) 92%, white 8%)',
    boxShadow:
      '0 1px 2px rgba(var(--ion-color-dark-rgb), 0.05), 0 4px 10px -4px rgba(var(--ion-color-dark-rgb), 0.10)',
  },
  workspaceItemInteractive: {
    transition: 'background-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      boxShadow:
        '0 1px 2px rgba(var(--ion-color-dark-rgb), 0.06), 0 7px 16px -6px rgba(var(--ion-color-dark-rgb), 0.14)',
    },
    '&:active': {
      background: 'var(--ion-color-light-tint)',
      boxShadow: '0 1px 1px rgba(var(--ion-color-dark-rgb), 0.05)',
    },
  },
  avatar: {
    '--border-radius': '10px',
    width: 30,
    height: 30,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'ui-monospace, Menlo, monospace',
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.1,
    fontWeight: 600,
    color: 'var(--ion-color-dark)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  caption: {
    margin: 0,
    marginTop: 4,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11.5px',
    color: 'var(--ion-color-medium)',
  },
  chevron: {
    fontSize: 7,
    flexShrink: 0,
  },
  select: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    cursor: 'pointer',
    '--padding-start': 0,
    '--padding-end': 0,
  },
  popover: {
    '--width': 'min(320px, calc(100vw - 32px))',
    '& ion-list': {
      padding: 0,
    },
    '& ion-label': {
      fontSize: 14,
    },
  },
  notifications: {
    flexShrink: 0,
  },
});

const WorkspaceSelect: React.FC = () => {
  const classes = useStyles();
  const { workspaces, workspace, activateWorkspace } = useWorkspace();
  const canSwitch = workspaces.length > 1;

  return (
    <div className={clsx(classes.workspaceItem, canSwitch && classes.workspaceItemInteractive)}>
      <Avatar alt={workspace.label} className={classes.avatar} />
      <div className={classes.textBlock}>
        <p className={classes.title}>{workspace.label}</p>
        <p className={classes.caption}>
          {canSwitch ? 'Switch workspace' : 'Workspace'}
          {canSwitch && <IonIcon icon={chevronDownOutline} className={classes.chevron} />}
        </p>
      </div>
      {canSwitch && (
        <IonSelect
          aria-label="Switch workspace"
          interface="popover"
          interfaceOptions={{ cssClass: classes.popover }}
          value={workspace.id}
          onIonChange={(e) => activateWorkspace(e.detail.value)}
          className={classes.select}
        >
          {workspaces.map((w) => (
            <IonSelectOption key={w.id} value={w.id}>
              {w.label}
            </IonSelectOption>
          ))}
        </IonSelect>
      )}
    </div>
  );
};

const MenuTopPanel: React.FC = () => {
  const classes = useStyles();

  return (
    <div className={classes.row}>
      <WorkspaceSelect />
      <Notifications className={classes.notifications} />
    </div>
  );
};

export default MenuTopPanel;
